import { Box, Text, useColorModeValue, VStack, useBreakpointValue, IconButton, HStack, Heading, Icon, Tooltip, useDisclosure, Link, Flex } from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiFolder, FiLink } from 'react-icons/fi';
import { useEffect, useRef } from 'react';
import DropboxImport, { DropboxImportRef } from './DropboxImport';
import { FaQuestionCircle } from 'react-icons/fa';
import { isMobileDevice } from '../utils/deviceDetection';
import Logo from './Logo';
import TooltipKeyBox from './TooltipKeyBox';
import DirectFileLinkImportModal from './DirectFileLinkImportModal';

interface FileImportProps {
  onFilesSelected: (files: File[], urls: string[], shareableUrls: string[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onProgressChange?: (downloadedBytes: number, totalBytes: number, completedFiles?: number, totalFiles?: number) => void;
}

const LocalFileImport: React.FC<{ onFilesSelected: (files: File[], urls: string[], shareableUrls: string[]) => void }> = ({ onFilesSelected }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const iconSize = useBreakpointValue({ base: 80, md: 80 });

  const handleClick = () => {
    inputRef.current?.click();
  };

  // Expose the click handler for keyboard shortcuts
  useEffect(() => {
    (window as any).__localFileImportClick = handleClick;
    return () => {
      delete (window as any).__localFileImportClick;
    };
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onFilesSelected(files, files.map(() => ''), files.map(() => ''));
    }
  };

  return (
    <Box position="relative">
      <input
        type="file"
        ref={inputRef}
        onChange={handleFileChange}
        accept=".mp3,.wav,.ogg,.m4a,.aac"
        style={{ display: 'none' }}
        multiple
      />
      <Tooltip 
        label={
          <HStack spacing={2} color="gray.800">
            <Text>Import local files</Text>
            <TooltipKeyBox>L</TooltipKeyBox>
          </HStack>
        } 
        placement="top"
        offset={[0, -35]}
      >
        <IconButton
          aria-label="Import local files"
          icon={<FiFolder size={iconSize} />}
          colorScheme="blue"
          variant="ghost"
          onClick={handleClick}
          size="lg"
          width={{ base: '150px', md: '150px' }}
          height={{ base: '150px', md: '150px' }}
          borderRadius="2xl"
          _hover={{
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
        />
      </Tooltip>
    </Box>
  );
};

const DirectFileLinkImportButton: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const iconSize = useBreakpointValue({ base: 74, md: 74 });

  return (
    <Tooltip 
      label={
        <HStack spacing={2} color="gray.800">
          <Text>Add public download links from forums, websites or Dropbox</Text>
          <TooltipKeyBox>F</TooltipKeyBox>
        </HStack>
      } 
      placement="top"
      offset={[0, -35]}
    >
      <IconButton
        aria-label="Import direct file links"
        icon={<FiLink size={iconSize} />}
        colorScheme="blue"
        variant="ghost"
        onClick={onOpen}
        size="lg"
        width={{ base: '150px', md: '150px' }}
        height={{ base: '150px', md: '150px' }}
        borderRadius="2xl"
        _hover={{
          transform: 'scale(1.05)',
          borderColor: 'transparent'
        }}
        _active={{
          transform: 'scale(0.95)',
          borderColor: 'transparent'
        }}
      />
    </Tooltip>
  );
};

const FileImport: React.FC<FileImportProps> = ({ onFilesSelected, onLoadingChange, onProgressChange }) => {
  const dropboxImportRef = useRef<DropboxImportRef>(null);
  const { isOpen: isDirectLinkModalOpen, onOpen: onDirectLinkModalOpen, onClose: onDirectLinkModalClose } = useDisclosure();
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a']
    },
    onDrop: (files) => {
      onFilesSelected(files, files.map(() => ''), files.map(() => ''));
    },
    noClick: true // Disable click to open file picker
  });

  const isMobile = useBreakpointValue({ base: true, md: false });
  const contentSpacing = useBreakpointValue({ base: 1, md: 2 });
  const marginTop = useBreakpointValue({ base: 0, md: "-400px" });
  const textColor = "gray.500";

  useEffect(() => {
    // Load Dropbox SDK only when needed
    const loadDropboxSDK = () => {
      if (!document.getElementById('dropboxjs')) {
        const script = document.createElement('script');
        script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
        script.id = 'dropboxjs';
        script.setAttribute('data-app-key', '7avzllilqvi9a9p');
        document.body.appendChild(script);
      }
    };

    // Load SDKs when component mounts
    loadDropboxSDK();

    return () => {
      const dropboxScript = document.getElementById('dropboxjs');
      if (dropboxScript) {
        document.body.removeChild(dropboxScript);
      }
    };
  }, []);

  // Add keyboard shortcuts for L (local), D (dropbox), and F (direct file links)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip hotkeys when user is editing text
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.contentEditable === 'true')) {
        return;
      }
      
      // Only handle shortcuts on the import page (when no files are loaded)
      // Only handle plain L key, let browser handle ⌘L (address bar)
      if (event.key.toLowerCase() === 'l' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        // Trigger local file import
        if ((window as any).__localFileImportClick) {
          (window as any).__localFileImportClick();
        }
      } else if (event.key.toLowerCase() === 'd') {
        event.preventDefault();
        event.stopPropagation();
        console.log('[FileImport] D key pressed');
        // Simply click the button - same as if user clicked it manually
        const dropboxButton = document.querySelector('[data-testid="dropbox-import-button"]') as HTMLButtonElement;
        console.log('[FileImport] Dropbox button found:', !!dropboxButton, 'disabled:', dropboxButton?.disabled);
        if (dropboxButton && !dropboxButton.disabled) {
          console.log('[FileImport] Clicking Dropbox button');
          dropboxButton.click();
        } else {
          console.warn('[FileImport] Dropbox button not found or disabled');
        }
      } else if (event.key.toLowerCase() === 'f' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        console.log('[FileImport] F key pressed');
        // Open direct file links modal
        onDirectLinkModalOpen();
      }
    };

    // Use capture phase to ensure we catch it first
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onDirectLinkModalOpen]);

  return (
    <Box
      {...getRootProps()}
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="block"
      overflow="auto"
      py={4}
      sx={{
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: isDragActive ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
          transition: 'all 0.2s',
          pointerEvents: 'none',
          zIndex: 1
        }
      }}
    >
      <input {...getInputProps()} />
      <VStack 
        spacing={4}
        align="center"
        maxW="600px"
        px={2}
        position="relative"
        zIndex={2}
        width="100%"
        mx="auto"
      >
        <Box as="span" display="block" width={{ base: "123.75px", md: "123.75px" }} maxW="60vw" mx="auto" mt={{ base: 0, md: "30px" }}>
          <Logo />
        </Box>
        <Text fontSize="sm" color="#9DA0A7" textAlign="center" maxW="500px" lineHeight="1.4">
        <Text as="span" color="#ACD5F3">COMPARE</Text> level-matched audio files with blind-test modes.
        <br />
        <Text as="span" color="#ACD5F3">SHARE</Text> and receive feedback with time-stamped notes.
        </Text>
        <Link
          href="https://www.youtube.com/watch?app=desktop&v=loacW3hj-vc&feature=youtu.be"
          target="_blank"
          rel="noopener noreferrer"
          color="#FF8C00"
          fontSize="xs"
          fontWeight="semibold"
          textDecoration="underline"
          _hover={{ color: "#FF8C00" }}
        >
          DEMO VIDEO
        </Link>
        <Box
          position="relative"
          bg="gray.900"
          borderRadius="2xl"
          pt={{ base: 0.5, md: 2 }}
          pb={{ base: 4, md: 2 }}
          px={{ base: 4, md: 4 }}
          mt={4}
          mx={4}
          maxW="600px"
          border="2px solid"
          borderColor="#FFD700"
          boxShadow="0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 152, 0, 0.15)"
          sx={{
            animation: 'twinkleBorder 3s ease-in-out infinite',
            '@keyframes twinkleBorder': {
              '0%, 100%': {
                borderColor: '#FFD700',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 215, 0, 0.3), 0 0 30px rgba(255, 107, 107, 0.2)',
              },
              '33%': {
                borderColor: '#FF6B6B',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 107, 107, 0.3), 0 0 30px rgba(78, 205, 196, 0.2)',
              },
              '66%': {
                borderColor: '#4ECDC4',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(78, 205, 196, 0.3), 0 0 30px rgba(255, 215, 0, 0.2)',
              },
            },
          }}
        >
          {/* Mobile layout: HStack with Local and Dropbox only */}
          <VStack spacing={{ base: 0, md: 6 }} display={{ base: 'flex', md: 'none' }}>
            <HStack spacing={{ base: 4, md: 8 }} justify="center" alignItems="flex-start">
              <VStack spacing={1}>
                <LocalFileImport onFilesSelected={onFilesSelected} />
                <Text fontSize="xs" color="#9DA0A7" whiteSpace="pre-line" mt="-25px" mb={{ base: 6, md: 0 }} textAlign="center">
                  Import files stored{"\n"}
                  on your device
                </Text>
              </VStack>
              <VStack spacing={1}>
                <DropboxImport ref={dropboxImportRef} onFilesSelected={onFilesSelected} />
                <Text fontSize="xs" color="#9DA0A7" whiteSpace="pre-line" mt="-25px" mb={{ base: 6, md: 0 }} textAlign="center">
                  Import cloud-hosted files{"\n"}
                  to <Text as="span" color="#ACD5F3">enable setup sharing</Text>
                </Text>
              </VStack>
            </HStack>
          </VStack>
          {/* Desktop layout: unchanged */}
          <VStack spacing={0} display={{ base: 'none', md: 'flex' }}>
            <Box>
              <Text 
                fontSize="md" 
                color={isDragActive ? "accent.400" : "#ACD5F3"} 
                textAlign="center"
                transition="color 0.2s ease"
                display={{ base: 'none', md: 'block' }}
                mb={{ base: 0, md: -4 }}
              >
                {isDragActive
                  ? 'Drop the audio files here...'
                  : 'Drag and drop files here or use an import button.'}
              </Text>
              <VStack spacing={{ base: 0, md: -4 }} align="center" mt={{ base: -4, md: 0 }}>
              <VStack spacing={0}>
                <LocalFileImport onFilesSelected={onFilesSelected} />
                <Text fontSize="xs" color="#9DA0A7" whiteSpace="pre-line" mt="-25px" mb={0} textAlign="center">
                  Import files stored{"\n"}
                  on your device
                </Text>
              </VStack>
              <VStack spacing={0} align="center">
                <HStack spacing={{ base: 2, md: 2 }} justify="center" alignItems="center">
                  {!isMobileDevice() && <DirectFileLinkImportButton onOpen={onDirectLinkModalOpen} />}
                  <DropboxImport ref={dropboxImportRef} onFilesSelected={onFilesSelected} />
                </HStack>
                <Text fontSize="xs" color="#9DA0A7" whiteSpace="pre-line" mt="-17px" mb={{ base: 3, md: 4 }} textAlign="center">
                  Import files hosted online{"\n"}
                  to <Text as="span" color="#ACD5F3">enable setup sharing</Text>
                </Text>
              </VStack>
              </VStack>
            </Box>
          </VStack>
        </Box>
        <DirectFileLinkImportModal
          isOpen={isDirectLinkModalOpen}
          onClose={onDirectLinkModalClose}
          onFilesSelected={onFilesSelected}
          onLoadingChange={(loading) => {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileImport.tsx:378',message:'FileImport onLoadingChange callback called',data:{loading,hasCallback:!!onLoadingChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'M'})}).catch(()=>{});
            // #endregion
            onLoadingChange?.(loading);
          }}
          onProgressChange={(downloaded, total, completedFiles, totalFiles) => {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'FileImport.tsx:384',message:'FileImport onProgressChange callback called',data:{downloaded,total,completedFiles,totalFiles,hasCallback:!!onProgressChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'N'})}).catch(()=>{});
            // #endregion
            onProgressChange?.(downloaded, total, completedFiles, totalFiles);
          }}
        />
        <VStack spacing={1} mt={21}>
          <Text fontSize="sm" color="#9DA0A7">
            This site does not upload or store any data
          </Text>
          <Text fontSize="sm" color="#9DA0A7">
            {typeof window !== 'undefined' && isMobileDevice()
              ? 'Supports MP3, WAV, AAC/M4A, OGG and FLAC'
              : 'Supports MP3, WAV, AAC/M4A, OGG, OPUS and FLAC'}
          </Text>
          <Text fontSize="md" color="#9DA0A7" mt={2} fontWeight="medium" display={{ base: 'none', md: 'block' }}>
            <Text as="span" color="#ACD5F3" fontWeight="bold" fontSize="lg">NEW:</Text>{' '}
            <Text as="a" href="/patchplan" color="#ACD5F3" textDecoration="underline" fontSize="lg" fontWeight="semibold" _hover={{ color: "#7BB8E8" }}>
              PatchPlan
            </Text>
            {' '}<Text as="span" fontSize="md">- plan your patchbay and print labels</Text>
          </Text>
        </VStack>
        <Flex gap={4} mt={3} justify="center">
          <Link 
            href="/privacy" 
            color="gray.500" 
            fontSize="sm"
            _hover={{ textDecoration: 'underline', color: 'gray.400' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>
          <Link 
            href="/terms" 
            color="gray.500" 
            fontSize="sm"
            _hover={{ textDecoration: 'underline', color: 'gray.400' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </Link>
        </Flex>
      </VStack>
    </Box>
  );
};

export default FileImport; 